import React, { useState } from "react";
import { motion } from "framer-motion";

const fetchHTML = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Erreur lors du fetch : ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    console.error(`Erreur de scraping sur ${url}:`, error);
    return null;
  }
};

const scrapeWebsiteRecursively = async (
  url: string,
  visitedLinks: Set<string>,
  maxLinks: number
): Promise<string[]> => {
  const links: string[] = [];
  
  if (visitedLinks.size >= maxLinks) return links;
  if (visitedLinks.has(url)) return links;

  visitedLinks.add(url);

  const html = await fetchHTML(url);
  if (!html) return links;

  const regex = /href="(https:\/\/[^"]+)"/g;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const link = match[1];
    if (link.startsWith("https://news.un.org") && !visitedLinks.has(link)) {
      links.push(link);
      const subLinks = await scrapeWebsiteRecursively(link, visitedLinks, maxLinks);
      links.push(...subLinks);
      if (links.length >= maxLinks) break;
    }
  }

  return links;
};

const containerVariants: any = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.3,
    },
  },
};

const itemVariants: any = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5 },
  },
};

const Articles: React.FC = () => {
  const [links, setLinks] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const startScraping = async () => {
    setLoading(true);
    setLinks([]);
    setError(null);

    const visitedLinks = new Set<string>();
    try {
      const maxLinks = 150;
      const startingUrl = "https://news.un.org/fr/";
      const scrapedLinks = await scrapeWebsiteRecursively(startingUrl, visitedLinks, maxLinks);
      setLinks(scrapedLinks);
    } catch (error) {
      setError("Erreur lors du scraping.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6 }}
      style={{ padding: "2rem" }}
    >
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.6 }}
        style={{ color: "rgba(92, 225, 230, 1)" }}
      >
        News Scraper
      </motion.h1>

      <motion.button
        onClick={startScraping}
        disabled={loading}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
        style={{
          padding: "0.8rem 1.5rem",
          marginTop: "1rem",
          background: loading ? "rgba(92, 225, 230, 0.2)" : "rgba(92, 225, 230, 0.1)",
          border: "1px solid rgba(92, 225, 230, 0.5)",
          color: "rgba(92, 225, 230, 1)",
          borderRadius: "4px",
          cursor: loading ? "not-allowed" : "pointer",
          opacity: loading ? 0.6 : 1,
        }}
      >
        {loading ? "En cours de scraping..." : "Démarrer le scraping"}
      </motion.button>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ color: "#ff6b6b", marginTop: "1rem" }}
        >
          {error}
        </motion.p>
      )}

      <motion.ul
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ listStyle: "none", padding: 0, marginTop: "2rem" }}
      >
        {links.length > 0 ? (
          links.map((link, index) => (
            <motion.li
              key={index}
              variants={itemVariants}
              style={{
                marginBottom: "0.8rem",
                padding: "1rem",
                background: "rgba(92, 225, 230, 0.05)",
                borderLeft: "3px solid rgba(92, 225, 230, 0.5)",
              }}
              whileHover={{ 
                x: 8, 
                background: "rgba(92, 225, 230, 0.1)",
              }}
            >
              <motion.a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "rgba(92, 225, 230, 0.8)",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  wordBreak: "break-all",
                }}
                whileHover={{ color: "rgba(92, 225, 230, 1)" }}
              >
                {link}
              </motion.a>
            </motion.li>
          ))
        ) : (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ color: "rgba(240, 248, 255, 0.5)" }}
          >
            Aucun lien trouvé ou erreur lors du scraping.
          </motion.p>
        )}
      </motion.ul>
    </motion.div>
  );
};

export default Articles;
